import type { SurveyAnswerValue, SurveyElementJson } from '@coorum-research/shared-common/types'
import type { QuestionRatingModelData } from '@coorum-research/shared-common/types/survey-json.type'
import type { SurveyModel } from 'survey-core'
import type { SurveyElementFormHandler } from './survey-element-form'

export class SurveyElementFormHandlerRating implements SurveyElementFormHandler {
  #elementJson: QuestionRatingModelData
  #elementResultRecord?: Record<string, SurveyAnswerValue>
  #surveyModel?: SurveyModel

  constructor(
    elementJson: SurveyElementJson,
    elementResultRecord?: Record<string, SurveyAnswerValue>,
    surveyModel?: SurveyModel,
  ) {
    this.#elementJson = elementJson as QuestionRatingModelData
    this.#elementResultRecord = elementResultRecord
    this.#surveyModel = surveyModel
  }

  get elementJson() {
    return this.#elementJson
  }

  get surveyModel() {
    return this.#surveyModel || null
  }

  get data() {
    return this.#elementResultRecord || {}
  }

  getValue(): SurveyAnswerValue {
    // elementResultRecordから値を取得（panel内の場合）
    if (this.#elementResultRecord) {
      return this.#elementResultRecord[this.#elementJson.name] ?? ''
    }

    // SurveyModelから値を取得（独立した質問の場合）
    if (this.#surveyModel) {
      return this.#surveyModel.getValue(this.#elementJson.name) ?? ''
    }

    return ''
  }

  getComment(): string {
    // elementResultRecordからコメントを取得（panel内の場合）
    if (this.#elementResultRecord) {
      const commentKey = `${this.#elementJson.name}-Comment`
      return (this.#elementResultRecord[commentKey] as string) || ''
    }

    // SurveyModelからコメントを取得（独立した質問の場合）
    if (this.#surveyModel) {
      return this.#surveyModel.getComment(this.#elementJson.name) || ''
    }

    return ''
  }

  setElementResultRecordValue(value: SurveyAnswerValue) {
    if (!this.#elementResultRecord) {
      this.#elementResultRecord = {}
    }

    this.#elementResultRecord[this.#elementJson.name] = value

    // 独立したrating質問の場合、SurveyJSのモデルにも値を設定
    if (this.#surveyModel) {
      this.#surveyModel.setValue(this.#elementJson.name, value)
    }
  }

  setElementResultRecordComment(comment: string) {
    if (!this.#elementResultRecord) {
      this.#elementResultRecord = {}
    }

    const commentKey = `${this.#elementJson.name}-Comment`
    this.#elementResultRecord[commentKey] = comment

    // 独立したrating質問の場合、SurveyJSのモデルにもコメントを設定
    if (this.#surveyModel) {
      this.#surveyModel.setComment(this.#elementJson.name, comment)
    }
  }

  validate(): {
    isValid: boolean
    errorMessage?: string
  } {
    const value = this.getValue()

    // 必須チェック
    if (this.#elementJson.isRequired && (value === undefined || value === null || value === '')) {
      return {
        isValid: false,
        errorMessage: 'この項目は必須です',
      }
    }

    // 値の範囲チェック（値が存在し、かつnull・空文字でない場合のみ）
    if (value !== undefined && value !== null && value !== '') {
      const numValue = Number(value)
      const rateMin = this.#elementJson.rateMin || 1
      const rateMax = this.#elementJson.rateMax || this.#elementJson.rateCount || 5

      if (numValue < rateMin || numValue > rateMax) {
        return {
          isValid: false,
          errorMessage: `${rateMin}から${rateMax}の範囲で選択してください`,
        }
      }
    }

    // SurveyJSモデルのバリデーションも実行（独立した質問の場合）
    if (this.#surveyModel) {
      const isValid = this.#surveyModel.validate()
      if (!isValid) {
        const question = this.#surveyModel.getQuestionByName(this.#elementJson.name)
        if (question && question.errors && question.errors.length > 0) {
          return {
            isValid: false,
            errorMessage: question.errors[0].text || '入力値が無効です',
          }
        }
      }
    }

    return { isValid: true }
  }
}
